import { get } from 'utils/requestSurveyManager';
import { getRefreshDataHeader } from "../utils/getRefreshDataHeader";
import { toSurveyManagerSlug } from 'utils/surveyManagerSlug';

export interface GetLayers {
  layers: Layer[];
}

export interface Layer {
  client: string;
  surveyType: string;
  layer: string;
  category: string;
  year: string;
  surveys: string[];
}

export interface GetSurveys {
  surveys: Survey[];
}

export interface Survey {
  id: string;
  slug: string;
  surveyType: string;
  runNumber: string;
  runIndex: number;
  surveyDate: string;
}

export interface GetSurvey {
  id: string;
  slug: string;
  surveyType: string;
  runNumber: string;
  runIndex: number;
  surveyDate: string;
  layers: string[];
}

export interface GetSurveyShapefiles {
  shapefiles: SurveyShapefile[];
}

export interface SurveyShapefile {
  id: string;
  slug: string;
  category: string;
  filePath: string;
  notes: string;
  dateGenerated: string;
}

export interface GetSurveyTypes {
  surveyTypes: SurveyType[];
}

export interface SurveyType {
  id: string;
  code: string;
  name: string;
  description: string;
  dateCreated: string;
  numberOfSurveys: number;
}

export interface GetClients {
  clients: Client[];
}

export interface Client {
  id: string;
  name: string;
  slug: string;
  beginningOfSurveyYear: string;
  description: string;
  dateCreated: string;
  numberOfSurveys: number;
}

export interface GetShapefiles {
  shapefiles: Shapefile[];
}

export interface Shapefile {
  id: string;
  slug: string;
  surveyType: string;
  category: string;
  filePath: string;
  notes: string;
  layers: string[];
  dateGenerated: string;
}

export interface GetLayerTypes {
  layerTypes: LayerType[];
}

export interface LayerType {
  id: string;
  name: string;
  slug: string;
  category: string;
  derived: boolean;
  derivedLayerTypes: string[];
  numberOfImages: number;
  numberOfSurveys: number;
  numberOfShapefiles: number;
}



const useSurveyManagerActions = () => {

  const getLayers = async (refreshData: boolean = false, options: { client?: string; surveyType?: string; year?: number } = {}): Promise<GetLayers> => {
    const headers = getRefreshDataHeader(refreshData);
    const params = {
      ...(options.client ? { client: toSurveyManagerSlug(options.client) } : {}),
      ...(options.surveyType ? { surveyType: toSurveyManagerSlug(options.surveyType) } : {}),
      ...(options.year !== undefined ? { year: options.year } : {}),
    };
    const config = {
      ...headers,
      ...(Object.keys(params).length > 0 && {
        params,
      }),
    };

    try {
      const response = await get<GetLayers>(
        'survey-manager/layers',
        config
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch survey manager layers:", error);
      throw error;
    }
  };

  const getSurveys = async (refreshData: boolean = false, options: { client?: string; surveyType?: string; } = {}): Promise<GetSurveys> => {
    const headers = getRefreshDataHeader(refreshData);
    const params = {
      ...(options.client ? { client: toSurveyManagerSlug(options.client) } : {}),
      ...(options.surveyType ? { surveyType: toSurveyManagerSlug(options.surveyType) } : {}),
    };
    const config = {
      ...headers,
      ...(Object.keys(params).length > 0 && {
        params,
      }),
    };

    try {
      const response = await get<GetSurveys>(
        'survey-manager/surveys',
        config
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch survey manager surveys:", error);
      throw error;
    }

  };

  const getSurvey = async (slug: string, refreshData: boolean = false,): Promise<GetSurvey> => {
    const headers = getRefreshDataHeader(refreshData);
    const config = {
      ...headers,
    };
    const pathSlug = toSurveyManagerSlug(slug);

    try {
      const response = await get<GetSurvey>(
        `survey-manager/surveys/${pathSlug}`,
        config
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch survey ${pathSlug}:`, error);
      throw error;
    }
  };

  const getSurveyShapefiles = async (slug: string, refreshData: boolean = false, options: { category?: string; } = {}): Promise<GetSurveyShapefiles> => {
    const headers = getRefreshDataHeader(refreshData);
    const config = {
      ...headers,
      ...(Object.keys(options).length > 0 && {
        params: options,
      }),
    };
    const pathSlug = toSurveyManagerSlug(slug);

    try {
      const response = await get<GetSurveyShapefiles>(
        `survey-manager/surveys/${pathSlug}/shapefiles`,
        config
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch survey ${pathSlug} shapefiles:`, error);
      throw error;
    }
  };

  const getSurveyTypes = async (refreshData: boolean = false,): Promise<GetSurveyTypes> => {
    const headers = getRefreshDataHeader(refreshData);
    const config = {
      ...headers,
    };

    try {
      const response = await get<GetSurveyTypes>(
        'survey-manager/survey-types',
        config
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch survey manager survey types:", error);
      throw error;
    }
  };

  const getSurveyType = async (slug: string, refreshData: boolean = false): Promise<SurveyType> => {
    const headers = getRefreshDataHeader(refreshData);
    const config = {
      ...headers,
    };
    const pathSlug = toSurveyManagerSlug(slug);

    try {
      const response = await get<SurveyType>(
        `survey-manager/survey-types/${pathSlug}`,
        config
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch survey type ${pathSlug}:`, error);
      throw error;
    }
  };

  const getClients = async (refreshData: boolean = false,): Promise<GetClients> => {
    const headers = getRefreshDataHeader(refreshData);
    const config = {
      ...headers,
    };

    try {
      const response = await get<GetClients>(
        'survey-manager/clients',
        config
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch survey manager clients:", error);
      throw error;
    }
  };

  const getClient = async (slug: string, refreshData: boolean = false): Promise<Client> => {
    const headers = getRefreshDataHeader(refreshData);
    const config = {
      ...headers,
    };
    const pathSlug = toSurveyManagerSlug(slug);

    try {
      const response = await get<Client>(
        `survey-manager/clients/${pathSlug}`,
        config
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch client ${pathSlug}:`, error);
      throw error;
    }
  };

  const getShapefiles = async (refreshData: boolean = false, options: { client?: string; category?: string;} = {}): Promise<GetShapefiles> => {
    const headers = getRefreshDataHeader(refreshData);
    const params = {
      ...(options.client ? { client: toSurveyManagerSlug(options.client) } : {}),
      ...(options.category ? { category: options.category } : {}),
    };
    const config = {
      ...headers,
      ...(Object.keys(params).length > 0 && {
        params,
      }),
    };

    try {
      const response = await get<GetShapefiles>(
        'survey-manager/shapefiles',
        config
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch survey manager shapefiles:", error);
      throw error;
    }
  };

  const getShapefile = async (slug: string, refreshData: boolean = false,): Promise<Shapefile> => {
    const headers = getRefreshDataHeader(refreshData);
    const config = {
      ...headers,
    };
    const pathSlug = toSurveyManagerSlug(slug);

    try {
      const response = await get<Shapefile>(
        `survey-manager/shapefiles/${pathSlug}`,
        config
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch shapefile ${pathSlug}:`, error);
      throw error;
    }
  };

  const getLayerTypes = async (refreshData: boolean = false, options: { client?: string; } = {}): Promise<GetLayerTypes> => {
    const headers = getRefreshDataHeader(refreshData);
    const params = {
      ...(options.client ? { client: toSurveyManagerSlug(options.client) } : {}),
    };
    const config = {
      ...headers,
      ...(Object.keys(params).length > 0 && {
        params,
      }),
    };

    try {
      const response = await get<GetLayerTypes>(
        'survey-manager/layer-types',
        config
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch survey manager layer types:", error);
      throw error;
    }
  };

  const getLayerType = async (slug: string, refreshData: boolean = false): Promise<LayerType> => {
    const headers = getRefreshDataHeader(refreshData);
    const config = {
      ...headers,
    };
    const pathSlug = toSurveyManagerSlug(slug);

    try {
      const response = await get<LayerType>(
        `survey-manager/layer-types/${pathSlug}`,
        config
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch layer type ${pathSlug}:`, error);
      throw error;
    }
  };

  return {
    getLayers,
    getSurveys,
    getSurvey,
    getSurveyShapefiles,
    getSurveyTypes,
    getSurveyType,
    getClients,
    getClient,
    getShapefiles,
    getShapefile,
    getLayerTypes,
    getLayerType,
  };
};

export { useSurveyManagerActions };